import {
  BAND_LABEL,
  SUPPLIER_CATEGORIES,
  SUPPLIER_CATEGORY_LABEL,
  type SupplierBook,
  type SupplierCategory,
  type SupplierDetail,
} from '@cyberscore/contracts';
import { useQuery } from '@tanstack/react-query';
import { Network } from 'lucide-react';
import { api } from '../../lib/api';
import { hashQuery, href, navigate, useHash } from '../../lib/router';
import { useSession } from '../../lib/session';
import { Drawer } from '../../ui/Overlay';
import { money } from '../clients/ClientRecord';

const pct = (n: number) => (n > 0 && n < 0.005 ? '<1%' : `${Math.round(n * 100)}%`);
const TP01: Record<string, string> = { yes: 'Yes', partly: 'Partly', no: 'No', not_sure: 'Not sure' };

function query(p: { category?: string | null; q?: string | null; open?: string | null }) {
  const s = new URLSearchParams();
  for (const [k, v] of Object.entries(p)) if (v) s.set(k, v);
  const out = s.toString();
  return out ? `?${out}` : '';
}

/**
 * Suppliers shared across the book (task 75, decision 0054): who the insured businesses rely on for IT,
 * cloud, email, accounting and payments, how much of the book each supplier touches, and who is affected
 * if one is breached. Worked out from the businesses' own answers, under the reader's access.
 */
export function SuppliersPage() {
  const { tenant } = useSession();
  const params = hashQuery(useHash());
  const category = (SUPPLIER_CATEGORIES as readonly string[]).includes(params.get('category') ?? '') ? (params.get('category') as SupplierCategory) : null; // prettier-ignore
  const q = params.get('q') ?? '';
  const open = params.get('open');
  // From the address as it is now, not as last rendered: typing straight after a click mustn't undo it.
  const go = (p: { category?: string | null; q?: string | null; open?: string | null }) => {
    const now = hashQuery(window.location.hash);
    navigate(
      href.suppliers(query({ category: now.get('category'), q: now.get('q'), open: now.get('open'), ...p })),
    );
  };
  const book = useQuery({ queryKey: ['suppliers', tenant?.id], queryFn: () => api<SupplierBook>('GET', '/suppliers'), enabled: !!tenant }); // prettier-ignore
  const b = book.data;
  const rows = (b?.suppliers ?? []).filter((s) => (!category || s.categories.includes(category)) && (!q || s.name.toLowerCase().includes(q.toLowerCase()))); // prettier-ignore

  return (
    <>
      <div className="page-head">
        <h1>Suppliers</h1>
        <p>Who your insured businesses rely on, from their own answers: how much of the book each supplier touches, and who is affected if one is breached.</p>
      </div>
      {b && <p className="muted small">{b.answered} of {b.total} clients named their suppliers. New checks ask; earlier ones may not have.</p>}
      <div className="row gap wrap list-tools">
        <div className="seg list-tabs" role="group" aria-label="Used for">
          <button type="button" aria-pressed={!category} onClick={() => go({ category: null })}>All</button>
          {SUPPLIER_CATEGORIES.map((c) => <button key={c} type="button" aria-pressed={category === c} onClick={() => go({ category: c })}>{SUPPLIER_CATEGORY_LABEL[c]}</button>)}
        </div>
        <input className="input" aria-label="Find a supplier" placeholder="Find a supplier" value={q} onChange={(e) => go({ q: e.target.value || null })} />
      </div>
      <section className="card">
        {book.isPending ? <div className="empty">Loading…</div> : !rows.length ? (
          <div className="empty"><Network size={28} /><strong>{b?.suppliers.length ? 'No supplier matches' : 'No suppliers named yet'}</strong>Businesses name their IT, cloud, accounting and payment providers in the cyber check (optional questions in “Data”).</div>
        ) : (
          <div className="table-wrap">
            <table className="grid">
              <thead><tr><th>Supplier</th><th>Used for</th><th className="num">Clients</th><th className="num">Sum insured</th><th className="num">Premium</th></tr></thead>
              <tbody>
                {rows.map((s) => (
                  <tr key={s.key}>
                    <td><button type="button" className="linklike cell-title" onClick={() => go({ open: s.key })}>{s.name}</button></td>
                    <td className="small">{s.categories.map((c) => SUPPLIER_CATEGORY_LABEL[c]).join(', ')}</td>
                    <td className="num">{s.clients} <span className="muted small">{pct(s.share)}</span></td>
                    <td className="num">{s.sum_insured ? money(s.sum_insured) : '—'}</td>
                    <td className="num">{s.premium ? money(s.premium) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      {open && <SupplierDrawer supplierKey={open} onClose={() => go({ open: null })} />}
    </>
  ); // prettier-ignore
}

/** If this supplier is breached: the insureds that rely on it, biggest sum insured first. */
function SupplierDrawer({ supplierKey, onClose }: { supplierKey: string; onClose: () => void }) {
  const { tenant } = useSession();
  const d = useQuery({ queryKey: ['supplier', tenant?.id, supplierKey], queryFn: () => api<SupplierDetail>('GET', `/suppliers/${encodeURIComponent(supplierKey)}`), enabled: !!tenant, retry: false }); // prettier-ignore
  const s = d.data;
  return (
    <Drawer title={s ? `If ${s.name} is breached` : 'Supplier'} subtitle={s ? `${s.clients.length === 1 ? '1 client relies' : `${s.clients.length} clients rely`} on it · ${s.categories.map((c) => SUPPLIER_CATEGORY_LABEL[c]).join(', ')}` : undefined} onClose={onClose} footer={<><span className="grow" /><button type="button" className="btn" onClick={onClose}>Close</button></>}>
      {d.isPending ? <p className="muted">Loading…</p> : d.isError || !s ? <p className="muted">No client names this supplier.</p> : (
        <>
          <dl className="facts">
            <div><dt>Sum insured</dt><dd>{s.sum_insured ? money(s.sum_insured) : '—'}</dd></div>
            <div><dt>Premium</dt><dd>{s.premium ? money(s.premium) : '—'}</dd></div>
          </dl>
          <div className="table-wrap">
            <table className="grid small supplier-clients">
              <thead><tr><th>Client</th><th>Uses it for</th><th className="num">CyberScore</th><th title="TP-01: does it know which suppliers can access its systems?">Knows its suppliers</th><th className="num">Sum insured</th></tr></thead>
              <tbody>
                {s.clients.map((c) => (
                  <tr key={c.customer_key}>
                    <td><a className="cell-title" href={href.assessment(c.assessment_id)}>{c.customer_name}</a></td>
                    <td>{c.categories.map((k) => SUPPLIER_CATEGORY_LABEL[k]).join(', ')}</td>
                    <td className="num">{c.score === null ? '—' : <>{c.score} <span className="muted">{c.band ? BAND_LABEL[c.band as keyof typeof BAND_LABEL] : ''}</span></>}</td>
                    <td>{c.tp01 ? (TP01[c.tp01] ?? c.tp01) : <span className="muted">Not answered</span>}</td>
                    <td className="num">{c.sum_insured === null ? '—' : money(c.sum_insured)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Drawer>
  ); // prettier-ignore
}
